<template>
  <Page>
    <div
      :class="[
        $style['product-detail'],
        { [$style['product-detail--inline']]: inline }
      ]">
      <SkipLinks
        :items="[
          {
            id: 'details',
            title: 'Go to product details'
          },
          {
            id: 'size-selector',
            title: 'Go to size selector'
          },
          {
            id: 'footer',
            title: 'Go to footer'
          }
        ]" />

      <Head v-if="product">
        <Title>{{ metaData.pageTitle }}</Title>

        <Meta hid="description" name="description" vmid="description" :content="metaData.metaDescription" />
        <Meta
          v-for="tag in formatOpenGraph(metaData.openGraph)"
          :key="tag.hid"
          :property="tag.property"
          :content="tag.content" />
        <Link
          v-for="(tag) in formatCanonical(metaData)"
          :key="tag.key"
          :rel="tag.rel"
          :hreflang="tag.hreflang"
          :href="tag.href" />
      </Head>

      <!-- Keep it outside of the product-detail__content. Fixed elements don't work well inside of elements with translate3d on Safari -->
      <StickOnScroll :test-element="addToCartRef" :trigger-hide-element="endOfContent" :show-on-desktop="true">
        <ProductMiniHeader :product="product">
          <template #right>
            <Btn
              :disabled="sizesInStock.length === 0"
              :icon-left="smallScreen ? '' : 'Finance/shopping-cart-line'"
              color="cta"
              shape="pill"
              full-width="all"
              bold
              @click="addToCart">
              {{ $t('general/in-shopping-bag') }}
            </Btn>
          </template>
        </ProductMiniHeader>
      </StickOnScroll>

      <section
        :class="$style['product-detail__image']">
        <MediaGrid
          :loading="pending"
          :inline="inline"
          :class="[
            { [$style['flex-start']]: product?.images.length < 3 },
            $style['media-grid']
          ]"
          :images="product?.images"
          :images-meta="product?.imagesMeta"
          :videos="product?.videos"
          :alt="`${product?.title} ${product?.colorName}`" />
      </section>
      <section
        v-if="product"
        id="details"
        data-test-id="product-details"
        :class="$style['product-detail__content']">
        <div :class="$style['padding-mobile']">
          <Btn
            size="small"
            data-test-id="back-button"
            icon-left="System/arrow-left-s-line"
            class="h-margin-bottom"
            :class="$style['back-button']"
            @click="hasHistory ? $router.go(-1) : $router.push({ name: $lang + '-index' })">
            {{ $t('general/go-back') }}
          </Btn>

          <ProductHeader :product="product" />

          <LazyProductColorVariations
            v-if="hasColorVariations && product?.colorVariations"
            class="h-margin-top-l"
            :class="$style['color-variations']"
            :product="product"
            :active="product.cId"
            :colors="product.colorVariations" />

          <LazyProductSizes
            id="size-selector"
            ref="productSizes"
            class="h-margin-top-l"
            :current-size="currentSize"
            :sizes="sizes"
            :size-type="sizeType"
            :show-subscription-button="showSubscriptionButton"
            :errors="errors"
            :product="product"
            @show-subscription-form="showSubscriptionForm = true"
            @input="size => selectSize(size)" />

          <!-- Subscribe form. No Lazy for now, produces bugs.-->
          <ClientOnly>
            <ProductSubscribeForm
              v-if="showSubscriptionForm"
              class="h-margin-top"
              data-test-id="subscribe-form"
              :product="product"
              :size="currentSize" />
          </ClientOnly>
          <!-- Cart button -->
          <div ref="addToCartRef" :class="$style['product-detail__add-to-cart']">
            <Btn
              id="add-to-cart"
              data-test-id="add-to-cart"
              :disabled="sizesInStock.length === 0"
              icon-left="Finance/shopping-cart-line"
              color="cta"
              shape="pill"
              size="large"
              bold
              @click="addToCart">
              {{ $t('general/in-shopping-bag') }}
            </Btn>

            <LazyButtonLike
              :product="product"
              data-test-id="wishlist-button"
              :aria-label="$t('general/add-to-wishlist')" />

            <LazyButtonShare v-if="metaData" :title="metaData.pageTitle" :url="useRequestURL().href" />
          </div>

          <div
            v-if="product?.desc"
            :class="$style['product-detail__description']"
            class="h-margin-top-l">
            {{ product.desc }}
          </div>

          <LazyCollapse
            v-if="specs"
            :title="specs.title"
            border
            strong
            class="h-margin-top-xl">
            <LazyProductSpecs
              :specs="specs" />
          </LazyCollapse>

          <IntersectionObserver root-margin="10px">
            <LazyProductStock
              v-if="product.shopStock"
              data-test-id="product-stock"
              class="h-margin-top"
              :stock="product.shopStock" />

            <template #fallback>
              <div :style="{'height': '130px'}" data-test-id="product-stock-fallback" class="skeleton h-margin-top" />
            </template>
          </IntersectionObserver>

          <IntersectionObserver root-margin="10px">
            <LazyProductUsps
              class="h-margin-top"
              :usps="usps" />
            <template #fallback>
              <div :style="{'height': '192px'}" class="skeleton h-margin-top" />
            </template>
          </IntersectionObserver>

          <IntersectionObserver root-margin="10px">
            <LazyProductRelated
              v-if="product?.related"
              class="h-margin-top-xl"
              :products="product.related.products"
              :title="product.related.title" />

            <template #fallback>
              <div :style="{ 'height': '560px' }" class="skeleton h-margin-top-xl" />
            </template>
          </IntersectionObserver>

          <IntersectionObserver root-margin="10px">
            <LazyProductInspiration
              v-if="product?.relatedMedia"
              class="h-margin-top-l"
              :items="product.relatedMedia" />

            <template #fallback>
              <div :style="{ 'height': '560px' }" class="skeleton h-margin-top-xl" />
            </template>
          </IntersectionObserver>

          <ClientOnly>
            <IntersectionObserver>
              <LazyProductRecent
                v-if="recentStore.items?.length"
                class="h-margin-top-l h-margin-bottom-l h-padding-mobile"
                :exclude="product.cId" />

              <template #fallback>
                <div :style="{ 'height': '560px' }" class="skeleton h-margin-top-xl" />
              </template>
            </IntersectionObserver>
          </ClientOnly>
        </div>
      </section>
    </div>
    <div ref="endOfContent" data-test-id="end-of-content" :class="$style['end-of-content']" />
  </Page>
</template>
<script setup>
import {
  formatCanonical,
  formatOpenGraph
} from '~/helpers/metadata'

const props = defineProps({
  id: {
    type: Number,
    default: null
  },
  slug: {
    type: String,
    default: null
  },
  inline: {
    type: Boolean,
    default: false
  }
})
const { $bus, $lang } = useNuxtApp()
const route = useRoute()
const cartStore = useCartStore()
const recentStore = useRecentStore()
const showSubscriptionForm = ref(false)
const showSubscriptionButton = ref(false)
const errors = ref([])
const addToCartRef = ref()
const endOfContent = ref()
const smallScreen = ref()
const productSizes = ref(null)
await useI18n(['general', 'account', 'product', 'usps'])
const { pending, sizes, sizeType, specs, usps, hasColorVariations, allSizesAvailable, sizesInStock, product, currentSize, metaData, loadProduct } = useProduct()

const hasHistory = computed(() => {
  return process.client && window.history.length > 1
})

const scrollToSizes = () => {
  const sizes = productSizes.value.$el
  if (!sizes) { return }
  const rect = sizes.getBoundingClientRect()

  window.scrollBy({
    left: 0,
    top: rect.top - 200,
    behavior: 'smooth'
  })
}

const addToCart = () => {
  try {
    if (!currentSize.value) {
      errors.value.size = $t('general/select-size')
      scrollToSizes()
      return
    }
    errors.value.size = null
    cartStore.show = true
    cartStore.add({
      product: product.value,
      size: currentSize.value
    })
    addToCartDataLayer()
  } catch (e) {
    console.error(e.message)
  }
}

const addToCartDataLayer = () => {
  $bus.$emit('add-to-cart', {
    eventId: Date.now(),
    sourceUrl: document.location.href,
    product: product.value,
    size: currentSize.value
  })
}

const viewDetailsDataLayer = () => {
  $bus.$emit('view-details', {
    eventId: Date.now(),
    sourceComponent: 'productDetail',
    sourceUrl: route.path,
    product: product.value
  })
}

const selectSize = size => {
  currentSize.value = size
}

const init = async () => {
  const mediaQuery = window.matchMedia('(max-width: 360px)')
  smallScreen.value = mediaQuery.matches
  mediaQuery.addEventListener('change', () => {
    smallScreen.value = mediaQuery.matches
  })

  await nextTick()
  setTimeout(() => {
    if (window.scrollY > 0) {
    // Fix product detail page scroll position bug in keepalive internals
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, 40)
}

onActivated(() => {
  viewDetailsDataLayer()
  init()
})

onMounted(() => {
  init()
})

const stopSizeWatcher = watch(currentSize, () => {
  if (!currentSize.value) { return }
  showSubscriptionForm.value = !currentSize.value.stock
})

// Temp fix for ld+json on ssr. @TODO: Fix this
if (process.server) {
  try {
    await loadProduct(route.params.id || props.id)
  } catch (e) {
    showError({ statusCode: 404, statusMessage: 'Product Not Found' })
  }
  if (metaData?.value) {
    const schemaOrg = toRaw(metaData.value).schemaOrg
    useHead({
      titleTemplate: '%s',
      script: [
        ...schemaOrg.map(schemaEntry => {
          return {
            type: 'application/ld+json',
            children: JSON.stringify(schemaEntry),
            tagPosition: 'bodyClose'
          }
        })
      ]
    })
  }
  viewDetailsDataLayer()
} else {
  try {
    await loadProduct(route.params.id || props.id)
  } catch {
    showError({ statusCode: 404, statusMessage: 'Product Not Found' })
  }
  if (metaData?.value) {
    const schemaOrg = toRaw(metaData.value).schemaOrg
    useHead({
      titleTemplate: '%s',
      script: [
        ...schemaOrg.map(schemaEntry => {
          return {
            type: 'application/ld+json',
            children: JSON.stringify(schemaEntry),
            tagPosition: 'bodyClose'
          }
        })
      ]
    })
  }
  viewDetailsDataLayer()
}

const stopProductWatcher = watch(product, () => {
  if (!product.value) { return }
  if (product?.value) {
    recentStore.add(product.value.cId)
  }
  if (product.value?.sizes.length === 1) {
    currentSize.value = product.value.sizes[0]
  }

  if (!allSizesAvailable.value && product.value?.sizes.length > 1) {
    showSubscriptionButton.value = true
  }

  if (!sizesInStock.value?.length) {
    showSubscriptionForm.value = true
    showSubscriptionButton.value = false
  }

  if (
    Array.isArray(product.value?.tags) &&
    product.value?.tags.includes('backsoon')
  ) {
    showSubscriptionForm.value = true
  }
  if (
    Array.isArray(product.value?.tags) &&
    product.value.tags.includes('comingsoon')
  ) {
    showSubscriptionForm.value = true
  }
}, { immediate: true })

onBeforeUnmount(() => {
  stopProductWatcher()
  stopSizeWatcher()
})

</script>
<style lang="scss" module>
.product-detail {
  background-size: cover;
  background-attachment: fixed;
  background-position: 50% 50%;
  display: flex;
  margin: 0 auto;
  padding: 0;
  position: relative;

  @media screen and (prefers-color-scheme: darker) {
    background-color: $black;
  }

  @media (max-width: #{$breakpoint-small-desktop}) and (orientation: portrait) {
    flex-direction: column;
    padding-top: 0;
  }

  &__add-to-cart {
    display: grid;
    gap: $spacing;
    grid-template-columns: 1fr auto auto;
    margin: $spacing * 6 0 $spacing * 4 0;
    max-width: $breakpoint-mobile;
  }

  &__image {
    display: flex;
    flex-direction: row;
    padding: $spacing * 6;
    padding-right: 0;
    min-height: 100vh;
    transform: translate3d(0, 0, 1);
    width: 60%;

    @media (max-width: #{$breakpoint-small-desktop}) {
      min-height: auto;
      padding: $spacing * 4 1vw;
      width: 50%;
    }

    .media-grid {
      height: auto;
      width: 100%;
      position: sticky;
      bottom: calc(var(--toolbar-height) + #{$spacing * 4});
      align-self: flex-start;
    }

    @media screen and (prefers-color-scheme: darker) {
      opacity: 0.75;
      transition: opacity 1s ease;

      &:hover {
        opacity: 1;
      }
    }

    @media (max-width: #{$breakpoint-small-desktop}) and (orientation: portrait) {
      aspect-ratio: 10/16;
      padding: 0;
      position: sticky;
      top: 0;
      width: 100%;
      z-index: 1;

      .media-grid {
        align-self: flex-start;
      }
    }
  }

  &__content {
    padding: $spacing * 6;
    width: 40%;
    max-width: 800px;
    min-height: 100vh;

    @media (max-width: #{$breakpoint-small-desktop}) and (orientation: landscape) {
      padding: $spacing * 4 1vw;
      width: 50%;
    }

    @media (max-width: #{$breakpoint-small-desktop}) and (orientation: portrait) {
      background-color: $white;
      padding: 0;
      width: 100%;
      max-width: 100%;
      position: relative;
      transform: translate3d(0,0,0);
      z-index: 10; // Keep this here for ugly safari overscroll fix

      .padding-mobile {
        padding: $spacing * 2;
      }
    }
  }

  &__description {
    // color: $grey;
    // font-size: $font-size-m;
    font-weight: $font-weight-normal;
  }

  &--inline {
    flex-direction: column;
    padding-top: 0;

    .product-detail__image {
      width: 100%;
      padding: 0;
    }

    .product-detail__content {
      width: 100%;
      padding: 32px 2vw;
    }

    .back-button,
    .color-variations {
      display: none;
    }
  }
}

.button {
  &::before {
    content: '';
    width: 100%;
    height: $spacing * 5;
    background-color: $gold;
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>
